import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, ChevronLeft, ChevronRight, PenSquare } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { BlogCard } from '@/components/blog';
import { Button } from '@/components/ui/button';
import { useBlogPosts, useBlogCategories } from '@/hooks/useBlog';
import { useAuthStore } from '@/stores/auth.store';
import type { BlogFilters } from '@/types/blog';

export function BlogList() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [filters, setFilters] = useState<BlogFilters>({
    page: 1,
    limit: 12,
    sortBy: 'latest',
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading } = useBlogPosts(filters);
  const { data: categories } = useBlogCategories();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }));
  };

  const handleCategoryFilter = (categoryId: string | undefined) => {
    setFilters((prev) => ({ ...prev, categoryId, page: 1 }));
  };

  const handleSortChange = (sortBy: BlogFilters['sortBy']) => {
    setFilters((prev) => ({ ...prev, sortBy, page: 1 }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500 py-16">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
        <div className="container mx-auto px-4 text-center relative">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-white mb-3"
          >
            {t('blogPage.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl mx-auto"
          >
            {t('blogPage.subtitle')}
          </motion.p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-72 shrink-0 space-y-6">
            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('blogPage.searchPlaceholder')}
                className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </form>

            {/* Sort */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                <Filter className="h-4 w-4" />
                {t('blogPage.browse')}
              </h3>
              <div className="space-y-1">
                {(['latest', 'popular', 'top-rated'] as const).map((sort) => (
                  <button
                    key={sort}
                    onClick={() => handleSortChange(sort)}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      filters.sortBy === sort
                        ? 'bg-emerald-100 text-emerald-700 font-medium'
                        : 'hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    {t(`blogPage.sort.${sort}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories */}
            {categories && categories.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3">{t('blogPage.categories')}</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryFilter(undefined)}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      !filters.categoryId
                        ? 'bg-emerald-100 text-emerald-700 font-medium'
                        : 'hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    {t('blogPage.allCategories')}
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryFilter(cat.id)}
                      className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                        filters.categoryId === cat.id
                          ? 'bg-emerald-100 text-emerald-700 font-medium'
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                        {cat.postCount}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Admin link */}
            {user?.role === 'ADMIN' && (
              <div className="space-y-2">
                <Link to="/blog/create">
                  <Button className="w-full gap-2" size="sm">
                    <PenSquare className="h-4 w-4" />
                    {t('blogPage.createPost')}
                  </Button>
                </Link>
                <Link to="/blog/my-posts">
                  <Button variant="outline" className="w-full" size="sm">
                    {t('blogPage.myPosts')}
                  </Button>
                </Link>
              </div>
            )}
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {isLoading ? (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse rounded-xl border bg-card">
                    <div className="aspect-video bg-muted" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 w-20 bg-muted rounded" />
                      <div className="h-5 w-full bg-muted rounded" />
                      <div className="h-3 w-3/4 bg-muted rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : data && data.data.length > 0 ? (
              <>
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {data.data.map((post) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <BlogCard post={post} />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {data.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={data.page <= 1}
                      onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      {t('blogPage.pageOf', { current: data.page, total: data.totalPages })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={data.page >= data.totalPages}
                      onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <PenSquare className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-medium mb-1">{t('blogPage.noPosts')}</h3>
                <p className="text-sm text-muted-foreground">{t('blogPage.noPostsDescription')}</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
